import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MenuItemAdmin } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { Loading } from '../../ui/bits';
import { Sheet } from '../../ui/viz';
import { Scene, SceneError } from '../../catalog/art';
import { useToast } from '../../ui/toast';
import { peso } from '../../lib/format.js';
import { SLOW_MS } from '../ShopLayout';
import { menuSections, optionsOf, requiredGroupEmpty, setOptionAvailability, soldOutCount } from './lib/board.js';
import { grillKeys } from './keys';

/** Side sheet: mark modifier options sold out (e.g. "Spicy sauce") without leaving the board. */
export function MenuPanel({ open, shopId, onClose }: { open: boolean; shopId: string; onClose(): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = grillKeys.menu(shopId);
  const menu = useQuery({ queryKey: key, queryFn: () => api.shopMenu(shopId), refetchInterval: SLOW_MS, enabled: !!shopId && open });
  const sections = useMemo(() => menuSections(menu.data ?? []), [menu.data]);

  const toggle = useMutation({
    mutationFn: ({ optionId, isAvailable }: { optionId: string; isAvailable: boolean; label: string }) => api.setModifierOption(shopId, optionId, isAvailable),
    onMutate: async ({ optionId, isAvailable }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<MenuItemAdmin[]>(key);
      qc.setQueryData<MenuItemAdmin[]>(key, (old) => setOptionAvailability(old ?? [], optionId, isAvailable));
      return { prev };
    },
    onSuccess: (res, { label }) => {
      qc.setQueryData<MenuItemAdmin[]>(key, (old) => setOptionAvailability(old ?? [], res.id, res.isAvailable));
      toast(res.isAvailable ? `${label} is back on the menu.` : `${label} is sold out. Customers can't pick it.`);
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast(errorMessage(err, "Couldn't update the option. Try again."), 'error');
    },
  });
  const busyId = toggle.isPending ? (toggle.variables?.optionId ?? null) : null;

  return (
    <Sheet open={open} title="Sold-out options" onClose={onClose} actions={menu.data ? <span className={soldOutCount(menu.data) ? 'dv-chip bad' : 'dv-chip'}>{soldOutCount(menu.data)} out</span> : null}>
      {menu.isError ? (
        <SceneError error={menu.error} onRetry={() => void menu.refetch()} compact />
      ) : menu.isLoading ? (
        <Loading label="Loading the menu" rows={2} />
      ) : sections.length === 0 ? (
        <Scene scene="noProducts" compact title="No options to manage">
          Grill products with choices (like a sauce or add-ons) show up here.
        </Scene>
      ) : (
        <div className="stack s12">
          {sections.map((s) => (
            <section key={s.section} className="stack s8" aria-label={s.section}>
              <h3 className="dv-label">{s.section}</h3>
              {s.items.map((it) => (
                <div key={it.productId} className="gk-menu-item">
                  <b className="t-label">{it.name}</b>
                  {it.groups
                    .filter((g) => g.options.length)
                    .map((g) => (
                      <div key={g.code} className="stack s4">
                        <span className="dv-sub">
                          {g.name}
                          {g.minChoices > 0 ? ' · required' : ''}
                        </span>
                        {requiredGroupEmpty(g) ? (
                          <span className="pill st-warning gk-wrap">
                            Every {g.name.toLowerCase()} option is sold out, so customers can't order {it.name}.
                          </span>
                        ) : null}
                        <ul className="stack s4">
                          {optionsOf(g).map((o) => (
                            <li key={o.id}>
                              <button
                                type="button"
                                role="switch"
                                className="switch gk-opt"
                                aria-checked={o.isAvailable}
                                disabled={busyId === o.id}
                                onClick={() => toggle.mutate({ optionId: o.id, isAvailable: !o.isAvailable, label: `${it.name} · ${o.label}` })}
                              >
                                <span className="track" aria-hidden="true" />
                                <span className="grow stack s4">
                                  <span className="t-label">{o.label}</span>
                                  <span className="dv-sub">
                                    {o.isAvailable ? 'Available' : 'Sold out'}
                                    {o.priceCentavos ? ` · +${peso(o.priceCentavos)}` : ''}
                                  </span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </Sheet>
  );
}
